import { useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { DescribedEntity, Entity, Vertex } from '../../../../types';
import {
  EntityRawDataLegacy,
  FetchVertex,
} from '../../../graphql/queries/queries';
import { RawDataEntity } from '../../Modals/RawData/types';
import { useSnackbarNotification } from '../../notifications/SnackbarNotificationContext';
import { vertexToDescribedEntity } from '../../util/entityDrawerUtils';
import { DeleteEntity, UpdateEntity } from './queries';
import convertVertexToEntity from './utils/convertVertexToEntity';

interface Props {
  vertexId?: string;
  vertex?: Vertex;
  onEditEntity?: (entity: Entity) => void;
  onDeleteEntity?: (entityId: Entity['id']) => void;
}

export interface DescribedEntityHook {
  entity?: DescribedEntity;
  _legacyEntity?: Entity;
  rawData: RawDataEntity[];
  loading: boolean;
  hasError: boolean;
  updateEntity: (
    timestamp: number,
    properties: Entity['properties']
  ) => Promise<Entity | false>;
  deleteEntity: () => Promise<string | false>;
}

export const useDescribedEntityHook = ({
  vertex,
  vertexId,
  onEditEntity,
  onDeleteEntity,
}: Props): DescribedEntityHook => {
  const { error, loading: loadingVertex, data } = useQuery(FetchVertex, {
    variables: { id: vertexId },
    skip: !!vertex || !vertexId,
  });
  const entity: DescribedEntity | undefined = useMemo(() => {
    if (vertex) return vertexToDescribedEntity(vertex);
    if (!data?.vertex) return;
    return vertexToDescribedEntity(data.vertex);
  }, [data, vertex]);
  const { data: rawData, loading: loadingRawData } = useQuery(
    EntityRawDataLegacy,
    {
      variables: {
        entityId: entity?._id,
        source: entity?.source,
      },
      skip: !entity?._id,
    }
  );
  const [mutationUpdateEntity] = useMutation(UpdateEntity);
  const [mutationDeleteEntity] = useMutation(DeleteEntity);
  const notification = useSnackbarNotification();

  const updateEntity: DescribedEntityHook['updateEntity'] = async (
    timestamp,
    properties
  ) => {
    try {
      if (!entity) throw new Error('No entity found, update failed.');
      const {
        data: {
          updateEntity: { vertex: newVertex },
        },
      } = await mutationUpdateEntity({
        variables: { entityId: data?.vertex.entity._id, timestamp, properties },
      });
      onEditEntity?.(newVertex);
      return properties;
    } catch (error) {
      notification.error({
        message: `Failed to update entity with id=${entity?.displayName}`,
        autoHideDuration: 2500,
        error,
      });
      return false;
    }
  };

  const deleteEntity = async () => {
    if (!entity) throw new Error('No entity found, delete failed.');
    const entityId = entity._id;
    const response = await mutationDeleteEntity({
      variables: {
        entityId,
        timestamp: new Date().getTime(),
        hardDelete: false,
      },
    });
    const deletedEntityId = response.data.updateEntity;
    notification.success(
      `Successfully deleted entity with id=${entityId}`,
      2500
    );
    onDeleteEntity?.(deletedEntityId);
    return deletedEntityId;
  };
  return {
    hasError: !!error,
    loading: loadingVertex,
    entity,
    _legacyEntity: data?.vertex && convertVertexToEntity(data.vertex),
    rawData: (!loadingRawData && rawData?.entityRawDataLegacy?.payload) || [],
    updateEntity,
    deleteEntity,
  };
};
